import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';

import { hasRoles, EDIT_QUERY_COMMENTS_ROLES } from '../../../util/RoleUtil';

import { createQueryType, retrieveQueryTypes, viewQueryType } from '../../../services/QueryTypeService';

import { ToolTip } from '../../common/tool-tip/ToolTip';
import { SaveButton } from '../../common/button/save-button/SaveButton';

import './GenericQueryComments.css';

export const GenericQueryComments = ({ context, setSpinnerText, showToaster, toolTipsEnabled, editMode }) => {
  const [queryTypes, setQueryTypes] = useState([]);
  const [selectedQueryType, setSelectedQueryType] = useState(undefined);
  const [queryResponses, setQueryResponses] = useState(['']);

  const fetchQueryTypes = useCallback(async () => {
    setSpinnerText('Retrieving Query Types');
    const [data, error] = await retrieveQueryTypes(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        const formattedQueryTypes = data.queryTypes.map((queryType) => ({
          value: queryType,
          label: queryType.name,
        }));
        setQueryTypes(formattedQueryTypes);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [setSpinnerText, showToaster, context.username]);

  useEffect(() => {
    fetchQueryTypes();
  }, [fetchQueryTypes]);

  const fetchQueryTypeResponses = async (queryTypeExternalReference) => {
    setSpinnerText('Retrieving Query Type Responses');
    const [data, error] = await viewQueryType(context.username, queryTypeExternalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        if (data?.queryType?.availableResponses.length === 0) {
          setQueryResponses(['']);
        } else {
          setQueryResponses(data.queryType.availableResponses);
        }
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const saveGenericQueryComments = async () => {
    if (!selectedQueryType) {
      return;
    }

    const createQueryTypeRequest = {
      queryTypes: [
        {
          active: true,
          availableResponses: queryResponses,
          externalReference: selectedQueryType.value.externalReference,
          name: selectedQueryType.value.name,
        },
      ],
      username: context.username,
    };
    setSpinnerText('Creating Generic Query Comments');
    const [data, error] = await createQueryType(createQueryTypeRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Generic query comments created.',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleQueryTypeSelection = (query) => {
    setSelectedQueryType(query);
    fetchQueryTypeResponses(query.value.externalReference);
  };

  const handleSaveChangesClick = () => {
    saveGenericQueryComments();
  };

  const handleCancelClick = () => {
    setQueryResponses([]);
  };

  const handleAddSectionClick = () => {
    setQueryResponses((prevQuestions) => [...prevQuestions, '']);
  };

  const handleSectionInputChange = (index, value) => {
    setQueryResponses((prevQuestions) => {
      const newQuestions = [...prevQuestions];
      newQuestions[index] = value;
      return newQuestions;
    });
  };

  const handleDeleteSectionClick = (index) => {
    setQueryResponses((prevQuestions) => prevQuestions.filter((_, i) => i !== index));
  };

  const fieldsDisabled = !hasRoles(context, EDIT_QUERY_COMMENTS_ROLES);

  return (
    <div className="query-comments">
      <div className="query-comments-content">
        <h3 className="security-questions-header">Generic Query Comments</h3>
        <div className="input-container">
          <label className={'input-label-sm'}>Query Type:</label>
          <Select
            options={queryTypes}
            closeMenuOnSelect={true}
            onChange={handleQueryTypeSelection}
            value={selectedQueryType}
            classNamePrefix="input-select"
          />
          {toolTipsEnabled && (
            <div className="form-tooltips">
              <ToolTip
                tooltipExternalReference={'queryCommentsQueryType123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Query Type:'}
              />
            </div>
          )}
        </div>
        {selectedQueryType !== undefined && (
          <div className="security-questions-input-container">
            <label className={'input-label-sm'}>Responses:</label>
            <div className="query-comments-input-container-column">
              {queryResponses.map((section, index) => (
                <div key={index} className="query-comments-input-container">
                  <textarea
                    className="query-comments-input-area"
                    type="text"
                    value={section}
                    onChange={({ target }) => handleSectionInputChange(index, target.value)}
                    disabled={fieldsDisabled}
                  />
                  {queryResponses.length > 1 && (
                    <button
                      className="delete-section-button"
                      onClick={() => handleDeleteSectionClick(index)}
                      disabled={fieldsDisabled}
                    >
                      X
                    </button>
                  )}
                </div>
              ))}
            </div>
            {toolTipsEnabled && (
              <div className="form-tooltips">
                <ToolTip
                  tooltipExternalReference={'sections123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Responses:'}
                />
              </div>
            )}
          </div>
        )}
        {selectedQueryType !== undefined && (
          <div className="query-comments-add-button">
            <SaveButton title="Add" onClick={handleAddSectionClick} color="#077187" disabled={fieldsDisabled} />
          </div>
        )}
      </div>

      {!editMode && (
        <div className={'query-comments-button-container'}>
          <SaveButton
            color={'#ffffff'}
            textColor={'#077187'}
            border={'1px solid #077187'}
            title={'Cancel'}
            onClick={handleCancelClick}
          />
          <SaveButton
            color={'#077187'}
            title={'Save Changes'}
            onClick={handleSaveChangesClick}
            disabled={fieldsDisabled}
          />
        </div>
      )}
    </div>
  );
};
